import { select, call, put, takeLatest } from 'redux-saga/effects';
import debouncePromise from 'debounce-promise';
import { TrackeableEvent } from 'redux/modules/analytics';
import {
  HANDLEDBY_OPEN_ASSIGN_AGENT_MODAL,
  HANDLEDBY_CANCEL_ASSIGN_AGENT_MODAL,
  HANDLEDBY_SAVE_ASSIGN_AGENT_MODAL,
} from 'redux/modules/analytics/events';

import {
  assignHandlingAgent as assignHandlingAgentRequest,
  getOperators,
} from 'redux/modules/journeys/http';

import { requestWithUpdateHandler } from 'redux/modules/realtime/effects';

import { setLoading, setOpened, setClosed, setSelectedAgent } from './duck';
import { getOpenedId, getSelectedAgent, getHandledBy } from './selectors';

const openModal$ = 'journey-handling/openModal$';
const closeModal$ = 'journey-handling/closeModal$';
const assignAgent$ = 'journey-handling/assignAgent$';
const selectAgent$ = 'journey-handling/selectAgent$';

const getMe = (state) => state.me;

export const openHandlingModal = ({ id, handledBy }) => ({
  type: openModal$,
  payload: {
    id,
    handledBy,
  },
});

export const closeHandlingModal = () => ({
  type: closeModal$,
  payload: {},
});

export const assignAgent = () => ({
  type: assignAgent$,
  payload: {},
});

export const selectAgent = ({ agent }) => ({
  type: selectAgent$,
  payload: {
    agent,
  },
});

const getHandlingOperators =
  (filterUserGroups = '') =>
  async (input) => {
    if (!input || input.length < 3) {
      return Promise.resolve({ options: [] });
    }

    const filteredGroups = filterUserGroups.split(',');

    const staffUsers = await getOperators(input);
    const staffUsersFiltered = staffUsers.filter(({ groups }) =>
      groups.reduce((acc, group) => {
        if (!acc) {
          return false;
        }
        return !filteredGroups.includes(group);
      }, true)
    );

    const operatorsWithLabel = staffUsersFiltered.map((operator) => ({
      ...operator,
      value: operator.id,
      label: `${operator.first_name} ${operator.last_name}`,
    }));

    return Promise.resolve({ options: operatorsWithLabel || [] });
  };

export const debouncedGetHandlingOperators = (data) =>
  debouncePromise(getHandlingOperators(data), 500, {
    leading: true,
  });

const handlingEvent = new TrackeableEvent();

function* handlingModalOpen({ payload }) {
  const { id, handledBy } = payload;
  yield put(setOpened({ id, handledBy }));

  const { id: meId } = yield select(getMe);

  yield call(handlingEvent.start, HANDLEDBY_OPEN_ASSIGN_AGENT_MODAL, {
    journeyId: id,
    hasOwner: !!handledBy,
    isMine: !!handledBy && handledBy.id === meId,
  });
}

function* handlingModalClose() {
  const id = yield select(getOpenedId);

  yield put(setClosed());

  yield call(handlingEvent.end, HANDLEDBY_CANCEL_ASSIGN_AGENT_MODAL, {
    journeyId: id,
  });
}

function* handleAssignment() {
  yield put(setLoading(true));

  const id = yield select(getOpenedId);
  const { id: meId } = yield select(getMe);
  const newAgent = yield select(getSelectedAgent);
  const handledBy = yield select(getHandledBy);

  yield call(handlingEvent.end, HANDLEDBY_SAVE_ASSIGN_AGENT_MODAL, {
    journeyId: id,
    hasOwner: !!handledBy,
    isMine: !!handledBy && handledBy.id === meId,
    isSelfAssignment: !!newAgent && newAgent.id === meId,
  });

  yield call(requestWithUpdateHandler, {
    model: 'journey',
    endpoint: assignHandlingAgentRequest,
    params: [id, newAgent],
  });

  yield put(setClosed());
  yield put(setLoading(false));
}

function* handleSelect({ payload }) {
  const { agent } = payload;
  yield put(setSelectedAgent({ agent }));
}

export function* watchJourneyHandling() {
  yield takeLatest(openModal$, handlingModalOpen);
  yield takeLatest(closeModal$, handlingModalClose);
  yield takeLatest(assignAgent$, handleAssignment);
  yield takeLatest(selectAgent$, handleSelect);
}
